/**
 *
 * Settings
 *
 */

import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux'

import { saveAs } from 'file-saver';

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import {
  flushStateSettings,
  loadSettings,
  changeSettings,
  deleteSettings,
} from './actions';

import {
  selectSettingsData,
  selectSettingsList,
  selectSettingsLoading,
  selectSettingsError,
  selectSettingsDeleteSuccessful,
} from './selectors';

// antd component
import { Button, Layout } from 'antd';
import { Content } from 'antd/es/layout/layout';
import CustomPageHeader from 'components/CustomPageHeader';
import CustomTable from 'components/CustomTable';
import { Link } from 'react-router-dom';
import api, { setAuthorizationHeader } from 'utils/apis/axiosAPI';
import { useAsync } from 'utils/hooks';
import { slugify } from 'utils/helpers/helper.general';
// assets
//import LogoClient from 'images/logo.svg';
//import CustomIcons from 'components/CustomIcons';

// global user
//import { withUser } from 'engine/Contexts/User.context';

export function Settings({
  // main data
  settingsList,
  settingsData,
  // states
  deleteSuccessful,
  error,
  loading,
  // actions
  loadSettings,
  changeSettings,
  flushState,
}) {
  useInjectReducer({ key: 'settings', reducer });
  useInjectSaga({ key: 'settings', saga });


  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  setAuthorizationHeader();

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // --> main code here
    !settingsList && loadSettings();

    // Anything in here is fired on component unmount.
    return () => {
      // Clearing the state after unmounting a component
      flushState();
    };
  }, []);


  const columns = [
    {
      title: 'Nom',
      dataIndex: 'lastname',
      key: 'column-lastname',
      className: 'column-lastname',
      sorter: (a, b) => a?.description?.lastname.localeCompare(b?.description?.lastname),
      render: (text, record) => {
        return {
          children: (
            <Link className="link-table" to={`/etudiants/${record._id}`}>
              {record?.description?.lastname}
            </Link>
          ),
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: 'Prénom',
      dataIndex: 'firstname',
      key: 'column-firstname',
      className: 'column-firstname',
      sorter: (a, b) => a?.description?.firstname.localeCompare(b?.description?.firstname),
      render: (text, record, index) => {
        return <Link className="link-table" to={`/etudiants/${record._id}`}>
          {record?.description?.firstname}
        </Link>
      },
    },
    {
      title: 'Date de naissance',
      dataIndex: 'birthdate',
      key: 'column-birthdate',
      className: 'column-birthdate',
      render: (text, record, index) => {
        return <Link className="link-table" to={`/etudiants/${record?._id}`}>
          {record?.description?.birthdate}
        </Link>
      },
    },
    {
      title: 'Classe',
      dataIndex: 'class_info',
      key: 'column-class_info',
      className: 'column-class_info',
      sorter: (a, b) => a?.class_info?.title.localeCompare(b?.class_info?.title),
      render: (text, record, index) => {
        return <Link className="link-table" to={`/classes/${record.class_info?._id}`}>
          {record?.class_info?.title}
        </Link>
      },
    },
    {
      title: 'Parents',
      dataIndex: 'parents',
      key: 'column-parents',
      className: 'column-parents',
      sorter: (a, b) => a?.parents?.length?.toString().localeCompare(b?.parents?.length?.toString()),
      render: (text, record, index) => {
        return record?.parents?.length
      },
    },
  ];


  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  const onClickHandle = async () => {
    setIsExporting(true);

    api.students.exportStudents(selectedRowKeys).then(result => {
      let csvContent = "data:text/csv;charset=utf-8,"
      + result.map(e => e.join(",")).join("\n");
      const encodedUri = encodeURI(csvContent);
      saveAs(encodedUri, slugify('students'))
     // window.open(encodedUri);
      setIsExporting(false);
    }).catch(err => {
      console.error(err);
      setIsExporting(false);
    })
  }

  // render function
  return (
    <div>
      <Layout className="main-full-content">
      <Helmet>
        <title>Audiences</title>
        <meta name="description" content="Description of Audiences" />
      </Helmet>
      <Content className="main-content-container">
        <div className="container-wrapped">
          <CustomPageHeader
            className="audiences-header"
            title="Paramètres"
            subTitle="Etudiants"
            subTitlePosition="top"
            extraActions={[
              <Button
                className="ant-btn-submit"
                type="default"
                disabled={loading || selectedRowKeys.length == 0}
                loading={isExporting}
                onClick={onClickHandle}
              >
                Exporter ({ selectedRowKeys?.length })
              </Button>,
            ]}
          />
          <CustomTable
            className="audiences-table"
            dataSource={settingsList?.data || []}
            columns={columns}
            loading={loading}
            rowSelection={rowSelection}
          />
        </div>
      </Content>
    </Layout>
    </div>
  );
}

Settings.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  deleteSuccessful: PropTypes.bool,
  loadSettings: PropTypes.func.isRequired,
  changeSettings: PropTypes.func,
  deleteSettings: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  loading: selectSettingsLoading(),
  error: selectSettingsError(),
  deleteSuccessful: selectSettingsDeleteSuccessful(),
  settingsList: selectSettingsList(),
  settingsData: selectSettingsData(),
});

function mapDispatchToProps(dispatch) {
  return {
    flushState: () => dispatch(flushStateSettings()),
    loadSettings: id => dispatch(loadSettings(id)),
    changeSettings: data => dispatch(changeSettings(data)),
    deleteSettings: id => dispatch(deleteSettings(id)),
    dispatch,
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(Settings);
